import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { PurchaseTable } from '@/components/purchases/purchase-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/clients';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { usePurchaseTransactions, type PurchaseRow } from '@/lib/purchases';
import { cn } from '@/lib/utils';

type Chip = 'open' | 'closed' | 'all';
const CHIPS: Chip[] = ['open', 'closed', 'all'];

export const Route = createFileRoute('/expenses/purchase-orders/')({
  component: PurchaseOrders,
  validateSearch: (s: Record<string, unknown>): { status?: Chip } =>
    s['status'] === 'closed' || s['status'] === 'all' ? { status: s['status'] as Chip } : {},
});

/** Purchase orders list with status chips (document 06 E10). */
function PurchaseOrders() {
  const t = useT();
  const orgId = useOrgId();
  const me = useMe();
  const navigate = useNavigate();
  const { status = 'open' } = Route.useSearch() as { status?: Chip };
  const [q, setQ] = useState('');
  const tx = usePurchaseTransactions();
  const needle = q.trim().toLowerCase();
  const rows = (tx.data ?? []).filter(
    (r: PurchaseRow) =>
      r.type === 'purchase_order' &&
      (status === 'all' || r.status === status) &&
      (!needle ||
        (r.contactName ?? '').toLowerCase().includes(needle) ||
        (r.number ?? '').toLowerCase().includes(needle) ||
        (r.memo ?? '').toLowerCase().includes(needle)),
  );
  const actions = me.data?.isStaff ? (
    <Button asChild>
      <Link to="/expenses/purchase-orders/new">
        {t('purchasePages.purchaseOrders.newPurchaseOrder')}
      </Link>
    </Button>
  ) : null;
  return (
    <>
      <PageHeader
        title={t('purchasePages.purchaseOrders.title')}
        description={t('purchasePages.purchaseOrders.description')}
        actions={actions}
      />
      {!orgId ? (
        <EmptyState
          title={t('shell.selectClient')}
          hint={t('purchasePages.listCommon.selectClientHint')}
        />
      ) : (
        <>
          <div
            className="mb-3 flex flex-wrap items-center gap-2"
            role="tablist"
            aria-label={t('purchasePages.listCommon.searchStatus')}
          >
            {CHIPS.map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={status === k}
                onClick={() =>
                  void navigate({
                    to: '/expenses/purchase-orders',
                    search: k === 'open' ? {} : { status: k },
                  })
                }
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  status === k
                    ? 'border-primary bg-accent font-medium text-accent-foreground'
                    : 'bg-card text-muted-foreground hover:bg-muted',
                )}
              >
                {t(`purchasePages.purchaseOrders.chips.${k}`)}
              </button>
            ))}
            <div className="relative ms-auto w-[340px]">
              <Search className="pointer-events-none absolute start-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card ps-8"
                placeholder={t('purchasePages.purchaseOrders.searchPlaceholder')}
                aria-label={t('purchasePages.purchaseOrders.searchLabel')}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
          {tx.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : tx.isError ? (
            <EmptyState
              title={t('purchasePages.purchaseOrders.loadError')}
              hint={tx.error.message}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              title={
                status === 'open'
                  ? t('purchasePages.purchaseOrders.noOpen')
                  : t('purchasePages.purchaseOrders.noneHere')
              }
              action={actions ?? undefined}
            />
          ) : (
            <PurchaseTable rows={rows} showType={false} />
          )}
        </>
      )}
    </>
  );
}
